/**
 * FooterLinksColumn — one named footer column of destinations: a tenant site's CMS footer menu, or
 * its active site links (LIFE-SHORTLISTED-001 P10.4, promoted here by Q-LS-32).
 *
 * It lived as a private `FooterColumn` inside purchase-web's TenantSiteFooter. A tenant's
 * server-rendered blog must draw the SAME footer as its site, and the blog is rendered by this
 * package's SSR harness from a serializable descriptor — so the column is a marketing-shell
 * component, reached from data through the `links` kind of `renderFooterExtraColumn`, and the tenant
 * site renders it through that same kind.
 *
 * Every link goes through SmartLink (internal / external / mailto / tel, the rel rules, the
 * outbound-click report). A `social` column (the site's own destinations — its site links) gives
 * every link the social-link style and a label span, and draws a SocialIcon for a link with a
 * `platform`; a menu column draws plain links. An empty column renders nothing — a heading over no
 * links is noise.
 *
 * @module @puffin-core/marketing-shell/components/FooterLinksColumn
 */

import { useId } from 'react';
import PropTypes from 'prop-types';

import { SmartLink } from '../links/SmartLink.jsx';
import { SocialIcon } from '../social/SocialIcon.jsx';

/**
 * @param {Object} props
 * @param {string} props.heading - the column's visible heading (it labels the landmark)
 * @param {ReadonlyArray<{label: string, href: string, platform?: string, destinationKind?: string}>} props.links
 * @param {boolean} [props.social=false] - the column lists the site's own destinations (site links)
 * @param {(href: string, destinationKind: string) => void} [props.onOutboundClick]
 * @returns {?React.JSX.Element}
 */
export function FooterLinksColumn({ heading, links, social = false, onOutboundClick }) {
  const id = useId();
  if (links.length === 0) return null;
  return (
    <nav className="footer-column-list" aria-labelledby={id}>
      <h3 id={id} className="footer-column-title">
        {heading}
      </h3>
      <ul>
        {links.map((link) => (
          <li key={link.href}>
            <SmartLink
              href={link.href}
              className={social ? 'footer-social-link' : undefined}
              destinationKind={link.destinationKind}
              onOutboundClick={onOutboundClick}
            >
              {social && link.platform ? <SocialIcon platform={link.platform} /> : null}
              {social ? <span>{link.label}</span> : link.label}
            </SmartLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

FooterLinksColumn.propTypes = {
  heading: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      platform: PropTypes.string,
      destinationKind: PropTypes.string,
    }),
  ).isRequired,
  social: PropTypes.bool,
  onOutboundClick: PropTypes.func,
};
